/**
 * Groups `ScheduledSlot` rows -- one per (account, job type) at one instant -- into one card per
 * (scheduled_slot, account), matching the Figma's account card, which carries every job type an
 * account reads at that slot together. Grouped by handle rather than `target_id`: two job types
 * for one account can carry different `target_id`s (a different media kind or page index), and
 * `account_handle`/`account_label` are the only fields both share.
 */

import type { LedgerStateValue, PlatformValue, ScheduledSlot } from './api/schemas';

export interface AccountJob {
  jobType: string;
  state: LedgerStateValue;
}

export interface AccountGroup {
  key: string;
  label: string;
  platform: PlatformValue | null;
  followerCount: number | null;
  jobs: AccountJob[];
}

export interface SlotGroup {
  scheduledSlot: string;
  accounts: AccountGroup[];
}

function accountKey(slot: ScheduledSlot): string {
  return slot.account_handle ?? slot.account_label ?? slot.target_id;
}

function accountLabel(slot: ScheduledSlot): string {
  return slot.account_label ?? slot.account_handle ?? slot.target_id;
}

export function groupSlots(slots: ScheduledSlot[]): SlotGroup[] {
  const bySlot = new Map<string, Map<string, AccountGroup>>();

  for (const slot of slots) {
    const accounts = bySlot.get(slot.scheduled_slot) ?? new Map<string, AccountGroup>();
    bySlot.set(slot.scheduled_slot, accounts);

    const key = accountKey(slot);
    const group = accounts.get(key) ?? {
      key,
      label: accountLabel(slot),
      platform: slot.platform,
      followerCount: slot.follower_count,
      jobs: [],
    };
    group.jobs.push({ jobType: slot.job_type, state: slot.state });
    accounts.set(key, group);
  }

  return Array.from(bySlot.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([scheduledSlot, accounts]) => ({
      scheduledSlot,
      accounts: Array.from(accounts.values()),
    }));
}

export interface SlotSummary {
  total: number;
  done: number;
  failed: number;
  /** Planned or dispatched: not yet a result either way. */
  pending: number;
}

export function slotSummary(group: SlotGroup): SlotSummary {
  const summary: SlotSummary = { total: 0, done: 0, failed: 0, pending: 0 };
  for (const account of group.accounts) {
    for (const job of account.jobs) {
      summary.total += 1;
      if (job.state === 'done') summary.done += 1;
      else if (job.state === 'failed') summary.failed += 1;
      else summary.pending += 1;
    }
  }
  return summary;
}
